import type { Model, StreamEvent, AgentKind, Effort } from '../agents/types';
import type { ModelProviderAdapter, StreamRequest } from './ModelProviderAdapter';
import { useUIStore } from '../store/uiStore';

/**
 * BackendCliAdapter — proxies to Loom's /api/v1/xoro/agents/* routes
 * which spawn and read tmux-backed CLI sessions. Implements the same
 * adapter interface as the API providers so runStream stays unchanged.
 *
 * One adapter instance per CLI kind; the singleton map at the bottom
 * keeps the right Model entry for each.
 */

/**
 * Resolve the API base URL.
 * - In production embedded mode, Xoro is served from the same origin
 *   as Loom, so a relative path works.
 * - In dev mode (localhost:5174) iframed by Loom, the iframe's origin
 *   is *not* the Loom origin. Loom appends `?host=<loom-origin>` to
 *   the iframe src; we use it as a prefix.
 */
function resolveAgentBase(): string {
  const RELATIVE = '/api/v1/xoro/agents';
  if (typeof window === 'undefined') return RELATIVE;
  try {
    const params = new URLSearchParams(window.location.search);
    const host = params.get('host');
    if (host && /^https?:\/\//.test(host)) {
      return `${host.replace(/\/$/, '')}${RELATIVE}`;
    }
  } catch { /* noop */ }
  return RELATIVE;
}

const AGENT_BASE = resolveAgentBase();

/**
 * Best-effort DELETE on the backend session — kills the underlying CLI
 * subprocess and removes its in-memory record on Loom. Failure is silent
 * because the frontend may carry sessions whose backend counterpart is
 * already gone (Loom restart, manual cleanup).
 */
export async function deleteBackendSession(sessionId: string): Promise<void> {
  try {
    await fetch(`${AGENT_BASE}/sessions/${sessionId}`, {
      method: 'DELETE',
      credentials: 'include',
    });
  } catch { /* noop */ }
}

class BackendCliAdapter implements ModelProviderAdapter {
  readonly id: string;
  readonly label: string;
  configured = true; // assumes Loom backend is reachable; runStream surfaces errors as system messages
  private agentKind: AgentKind;
  private models: Model[];

  constructor(agentKind: AgentKind, label: string, models?: Model[]) {
    this.id = agentKind;
    this.label = label;
    this.agentKind = agentKind;
    this.models = models ?? [
      { id: agentKind, label, providerId: agentKind, description: `${label} via terminal session` },
    ];
  }

  listModels(): Model[] {
    return this.models;
  }

  async *stream(req: StreamRequest): AsyncIterable<StreamEvent> {
    const sessionId = req.sessionId;
    const lastUserMessage = lastUserText(req.messages);

    // 1. ensure backend session exists (idempotent — POST returns existing if id matches)
    await this.ensureBackendSession(sessionId, req.effort);

    // 2. send the user message into the tmux session — include the
    // current permissionMode so live picker changes take effect on
    // existing sessions.
    if (lastUserMessage) {
      const permissionMode = useUIStore.getState().permissionMode;
      const r = await fetch(`${AGENT_BASE}/sessions/${sessionId}/messages`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ message: lastUserMessage, permissionMode, effort: req.effort }),
        signal: req.signal,
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.error ?? `messages POST ${r.status}`);
      }
    }

    // 3. open SSE stream for output, resuming from any prior offset
    const offsetKey = `xoro.cli.offset.${sessionId}`;
    const fromOffset = Number(localStorage.getItem(offsetKey) ?? '0') || 0;
    const url = `${AGENT_BASE}/sessions/${sessionId}/stream?since=${fromOffset}`;

    yield* this.consumeSse(url, offsetKey, req.signal);
  }

  cancel(): void {
    // SSE stream closes when the AbortSignal fires (passed in via req.signal in stream)
  }

  private async ensureBackendSession(id: string, effort: Effort): Promise<void> {
    const permissionMode = useUIStore.getState().permissionMode;
    const res = await fetch(`${AGENT_BASE}/sessions`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        id,
        agentKind: this.agentKind,
        permissionMode,
        // effort isn't used by the CLI directly today but we send it as
        // metadata so future adapters (claude --effort=…) can pick it up
        metadata: { effort },
      }),
    });
    if (!res.ok && res.status !== 200) {
      // 200 = reattached, 201 = created. Anything else fail.
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error ?? `sessions POST ${res.status}`);
    }
  }

  /** Translate Loom's SSE events back into StreamEvents the renderer consumes. */
  private async *consumeSse(
    url: string,
    offsetKey: string,
    signal?: AbortSignal
  ): AsyncIterable<StreamEvent> {
    const res = await fetch(url, { signal });
    if (!res.ok || !res.body) {
      throw new Error(`stream GET ${res.status}: ${res.statusText}`);
    }
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buf = '';

    try {
      while (true) {
        if (signal?.aborted) break;
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });

        // SSE events are double-newline separated
        let idx = buf.indexOf('\n\n');
        while (idx !== -1) {
          const raw = buf.slice(0, idx);
          buf = buf.slice(idx + 2);
          const ev = parseSseEvent(raw);
          if (ev) {
            // store offset so refresh resumes
            if (ev.type === 'sync' && typeof ev.offset === 'number') {
              try { localStorage.setItem(offsetKey, String(ev.offset)); } catch { /* noop */ }
            }
            // 'open' and 'sync' are housekeeping; pass through real StreamEvents
            if (ev.type !== 'open' && ev.type !== 'sync') {
              yield ev as StreamEvent;
            }
          }
          idx = buf.indexOf('\n\n');
        }
      }
    } finally {
      reader.releaseLock();
    }
  }
}

function lastUserText(messages: { role: string; parts: Array<{ type: string; text?: string }> }[]): string {
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i];
    if (m.role !== 'user') continue;
    return m.parts.filter((p) => p.type === 'text').map((p) => p.text ?? '').join('\n').trim();
  }
  return '';
}

function parseSseEvent(raw: string): { type: string;[k: string]: unknown } | null {
  let event = 'message';
  let data = '';
  for (const line of raw.split('\n')) {
    if (line.startsWith('event:')) event = line.slice(6).trim();
    else if (line.startsWith('data:')) data += line.slice(5).trim();
    else if (line.startsWith(':')) { /* keep-alive comment */ }
  }
  if (!data) return null;
  try {
    const parsed = JSON.parse(data) as Record<string, unknown>;
    return { type: event, ...parsed };
  } catch {
    return null;
  }
}

// Singletons — one per CLI kind. They share no state; each is just a configured shell.
// Model lists below mirror what each CLI's underlying provider exposes; the Xoro
// frontend treats them as labels and lets the backend translate label → CLI flag
// (e.g. --model, -m, -c model=…) when spawning. Effort lists live in the
// registry alongside provider info, since they vary per CLI.

export const claudeCliBackendAdapter = new BackendCliAdapter('claude_cli', 'Claude (CLI)', [
  { id: 'claude-opus-4-7',     label: 'Opus 4.7',      providerId: 'claude_cli' },
  { id: 'claude-opus-4-7-1m',  label: 'Opus 4.7',      providerId: 'claude_cli', badge: '1M' },
  { id: 'claude-sonnet-4-6',   label: 'Sonnet 4.6',    providerId: 'claude_cli' },
  { id: 'claude-haiku-4-5',    label: 'Haiku 4.5',     providerId: 'claude_cli' },
  { id: 'claude-opus-4-6',     label: 'Opus 4.6',      providerId: 'claude_cli', legacy: true },
]);

export const codexCliBackendAdapter = new BackendCliAdapter('codex_cli', 'Codex (CLI)', [
  { id: 'gpt-5.5',         label: 'GPT-5.5',        providerId: 'codex_cli' },
  { id: 'gpt-5.4',         label: 'GPT-5.4',        providerId: 'codex_cli' },
  { id: 'gpt-5.4-mini',    label: 'GPT-5.4-Mini',   providerId: 'codex_cli' },
  { id: 'gpt-5.3-codex',   label: 'GPT-5.3-Codex',  providerId: 'codex_cli' },
  { id: 'gpt-5.2',         label: 'GPT-5.2',        providerId: 'codex_cli' },
]);

export const geminiCliBackendAdapter = new BackendCliAdapter('gemini_cli', 'Gemini (CLI)', [
  { id: 'gemini-2.5-pro',         label: 'Gemini 2.5 Pro',         providerId: 'gemini_cli' },
  { id: 'gemini-2.5-flash',       label: 'Gemini 2.5 Flash',       providerId: 'gemini_cli' },
  { id: 'gemini-2.5-flash-lite',  label: 'Gemini 2.5 Flash-Lite',  providerId: 'gemini_cli' },
]);

export const hermesCliBackendAdapter   = new BackendCliAdapter('hermes',      'Hermes');
export const customCliBackendAdapter   = new BackendCliAdapter('custom_cli',  'Custom CLI');
